import { Box, Loader, SimpleGrid, Text } from "@mantine/core";
import { useTranslation } from "../../../hooks/useTranslation";
import { EmployeeOverviewListItem } from "../models/employeeOverview.model";
import { EmployeeOverviewCard } from "./EmployeeOverviewCard";

interface EmployeeOverviewGridProps {
    employees: EmployeeOverviewListItem[];
    loading: boolean;
    /** True whenever the side panel is open — for either a CV or a no-CV employee — so the
     *  column count reflects the narrower content area either way (not `personId`-specific,
     *  since a no-CV selection narrows the layout exactly the same way a CV one does). Renamed
     *  from `hasSelection` (Phase 4) to disambiguate from the unrelated bulk `selectionMode`
     *  concept below — the side panel and bulk-select mode are independent and can be true
     *  at the same time (mirrors CVList, where entering selection mode never force-closes an
     *  already-open preview panel). */
    isPanelOpen: boolean;
    selectionMode?: boolean;
    selectedIds?: Set<number>;
    onToggleSelect?: (employee: EmployeeOverviewListItem) => void;
    onSelectEmployee: (employee: EmployeeOverviewListItem) => void;
}

export function EmployeeOverviewGrid({
    employees,
    loading,
    isPanelOpen,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
    onSelectEmployee,
}: EmployeeOverviewGridProps) {
    const { t } = useTranslation();
    return (
        <SimpleGrid
            cols={{
                base: 1,
                sm: isPanelOpen ? 1 : 2,
                md: isPanelOpen ? 2 : 3,
                lg: isPanelOpen ? 3 : 4,
                xl: isPanelOpen ? 3 : 5,
            }}
            spacing="lg"
            p="xs"
            style={{ minHeight: "60vh", alignContent: "start" }}
        >
            {loading ? (
                <Box p="xl" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                    <Loader size="md" color="brand" />
                    <Text mt="md">{t("EMPLOYEEOVERVIEW_LOADING", undefined, "Loading employees...")}</Text>
                </Box>
            ) : employees.length === 0 ? (
                <Box p="xl" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                    <Text size="lg" fw={500} c="dimmed">
                        {t("EMPLOYEEOVERVIEW_NO_RESULTS", undefined, "No employees found matching your search criteria")}
                    </Text>
                </Box>
            ) : (
                employees.map((employee) => (
                    <EmployeeOverviewCard
                        key={employee.employeeId}
                        employee={employee}
                        selectionMode={selectionMode}
                        isSelected={selectedIds?.has(employee.employeeId)}
                        onToggleSelect={onToggleSelect}
                        onClick={() => onSelectEmployee(employee)}
                    />
                ))
            )}
        </SimpleGrid>
    );
}
